/*
 * Structured stub for nav items whose route exists but whose page hasn't
 * been built yet. Beats a blank placeholder by showing:
 *   - the page title from the nav config
 *   - the description (what this page will do once implemented)
 *   - the active phase tag so users know it's planned, not broken
 *
 * Lookup: by current pathname against both nav trees (workspace and
 * platform). Falls back to a generic message if nothing matches.
 */

import { useLocation, Link } from "react-router-dom";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { platformAdminNavigation } from "../navigation/platformAdminNavigation.js";
import { workspaceNavigation } from "../navigation/workspaceNavigation.js";
import type { NavItem, NavTree } from "../navigation/navigationTypes.js";

function findNavItem(pathname: string, trees: NavTree[]): NavItem | null {
  for (const tree of trees) {
    for (const group of tree.groups) {
      for (const item of group.items) {
        if (item.to === pathname) return item;
      }
    }
  }
  return null;
}

export default function ComingSoon() {
  const location = useLocation();
  const item = findNavItem(location.pathname, [platformAdminNavigation, workspaceNavigation]);
  const title = item?.label ?? "Coming soon";
  const description = item?.description ?? "This area is part of the navigation revamp and will be wired up in an upcoming phase.";

  return (
    <div className="page page-empty">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{title}</h1>
          <p className="muted">{description}</p>
        </div>
        <span className="pill pill-info" style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          Coming soon
        </span>
      </header>
      <section className="empty-state-card">
        <h2 style={{ marginTop: 0 }}>What this page will do</h2>
        <p>
          The information architecture for this surface is finalised — see <code>{location.pathname}</code> in the
          navigation registry — but the page implementation lands in a later phase. Until then, this stub
          confirms the route is reachable and exposes the planned scope so reviewers can validate the IA without
          waiting on the build.
        </p>
        <p className="muted" style={{ marginTop: 16 }}>
          Need this surface sooner? Open an issue against the TallyStream platform repo or ping the platform
          team — phases land in priority order.
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
          <Link to="/admin" className="btn btn-secondary">Back to admin overview</Link>
          <Link to="/app" className="btn btn-secondary">Back to workspace dashboard</Link>
        </div>
      </section>
    </div>
  );
}
